import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brand } from "@/config/site";

export default function ArcOfferDigital() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Your Dhamma ARC Offer | Dhamma Publishing House";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/arc/offer-digital/${token}`);
        setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        const d = err?.response?.data?.detail;
        setState({ loading: false, error: typeof d === "string" ? d : "This link is invalid or has expired.", data: null });
      }
    })();
  }, [token]);

  const respond = async (choice) => {
    setBusy(true);
    try {
      const res = await api.post(`/arc/offer-digital/${token}/respond`, { choice });
      setResult(res.data.status);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const answered = result || (state.data && ["Accepted", "Declined"].includes(state.data.offer_status)
    ? state.data.offer_status.toLowerCase() : null);

  return (
    <section className="min-h-screen bg-dh-bg px-5 pt-16 pb-24 sm:pt-20">
      <div className="mx-auto max-w-[640px]">
        <div className="flex flex-col items-center text-center">
          <img src={brand.crest} alt="Dhamma Publishing House" className="h-16 w-auto" />
          <p className="mt-5 font-body text-[11px] uppercase tracking-ultra text-dh-gold">Dhamma Publishing House</p>
          <h1 className="mt-4 font-display text-3xl leading-[1.15] text-dh-cream sm:text-4xl">Your Dhamma ARC — A Digital Offer</h1>
          <div className="gold-rule mx-auto mt-8 w-24" />
        </div>

        {state.loading ? (
          <p className="mt-16 text-center font-body text-sm text-dh-muted">Loading…</p>
        ) : state.error ? (
          <div data-testid="offer-error" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 text-center">
            <p className="font-body text-base leading-relaxed text-dh-cream/85">{state.error}</p>
          </div>
        ) : answered === "accepted" ? (
          <div data-testid="offer-accepted" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 text-center sm:p-10">
            <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Digital ARC Accepted</p>
            <h2 className="mt-4 font-display text-3xl text-dh-cream">Wonderful — welcome aboard.</h2>
            <p className="mx-auto mt-5 max-w-[480px] font-body text-base leading-relaxed text-dh-cream/80">
              You've accepted a Digital ARC. Dhamma Publishing House will follow up with next steps —
              no action is needed from you right now.
            </p>
          </div>
        ) : answered === "declined" ? (
          <div data-testid="offer-declined" className="mt-14 border border-dh-cream/15 bg-dh-forest/15 p-8 text-center sm:p-10">
            <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Offer Declined</p>
            <h2 className="mt-4 font-display text-3xl text-dh-cream">No problem at all.</h2>
            <p className="mx-auto mt-5 max-w-[480px] font-body text-base leading-relaxed text-dh-cream/80">
              We've noted your preference. Your application remains in good standing, and we appreciate
              your interest in Dhamma Publishing House.
            </p>
          </div>
        ) : (
          <div data-testid="offer-page" className="mt-12">
            <p className="text-center font-body text-base leading-relaxed text-dh-cream/80">
              {state.data.reviewer_name}, Print ARC availability is very limited for this release, and
              unfortunately we're unable to allocate a physical copy — but we would love to offer you a
              Digital ARC instead, with the same advance reading experience.
            </p>
            <p className="mt-5 text-center font-body text-sm text-dh-muted">
              Either choice is completely fine — your application remains in good standing.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <button data-testid="offer-accept" disabled={busy} onClick={() => respond("accept")}
                className="border border-dh-gold/70 bg-transparent px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                Accept Digital ARC
              </button>
              <button data-testid="offer-decline" disabled={busy} onClick={() => respond("decline")}
                className="border border-dh-cream/25 px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                Decline Digital
              </button>
            </div>
            <p className="mt-10 text-center font-body text-sm text-dh-muted">
              <Link to="/" className="text-dh-gold underline-offset-4 hover:underline">Return to Dhamma Publishing House</Link>
            </p>
          </div>
        )}

        <p className="mt-16 text-center font-body text-xs text-dh-muted">
          Questions? Contact{" "}
          <a href="mailto:Arcs@DhammaPublishingHouse.com" className="text-dh-gold underline-offset-4 hover:underline">Arcs@DhammaPublishingHouse.com</a>
        </p>
      </div>
    </section>
  );
}
